import { ReactNode } from "react";
import { Box, ButtonBase, Checkbox, Tooltip } from "@mui/material";
import { alpha, Theme, useTheme } from "@mui/material/styles";
import { ChevronRight } from "@mui/icons-material";

import type { ChipColor } from "utils/botStatusColors";

/** The colour a status chip of this tone is drawn in. */
export function statusToneColor(theme: Theme, color: ChipColor): string {
  return color === "default" ? theme.palette.text.secondary : theme.palette[color].main;
}

/** 100 only when the last employee is done, never a rounded 99.6. */
export function percentDone(success: number, error: number, total: number | null): number | null {
  if (total == null || total <= 0) return null;
  const processed = success + error;
  if (processed >= total) return 100;
  return Math.min(99, Math.floor((processed / total) * 100));
}

export interface StatusPillProps {
  label: string;
  color: ChipColor;
  title?: string;
  /** Same width in every list row, so the bars beside it line up. */
  minWidth?: number;
  onClick?: () => void;
  icon?: ReactNode;
}

// Statuses too long for a pill read shorter there; the full value is the tooltip.
const PILL_LABELS: Record<string, string> = { ECS_CONTAINER_STARTED: "STARTED" };

/** Outlined status chip: uppercase micro label in the status colour. */
export function StatusPill({ label, color, title, minWidth, onClick, icon }: StatusPillProps) {
  const theme = useTheme();
  const main = statusToneColor(theme, color);
  return (
    <Box
      component={onClick ? "button" : "span"}
      type={onClick ? "button" : undefined}
      title={title ?? label}
      onClick={onClick}
      sx={{
        flex: "0 0 auto",
        minWidth,
        maxWidth: "100%",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        height: 22,
        boxSizing: "border-box",
        px: 1,
        border: 1,
        borderColor: alpha(main, 0.7),
        borderRadius: "11px",
        bgcolor: "transparent",
        color: main,
        font: "inherit",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.5px",
        lineHeight: "20px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        cursor: onClick ? "pointer" : "inherit",
        // A pill that opens something says so before the hover does.
        textDecoration: onClick ? "underline dotted" : "none",
        textUnderlineOffset: "2px",
        "&:hover": onClick ? { bgcolor: alpha(main, 0.08), textDecoration: "underline" } : undefined,
        "& svg": { fontSize: 13 },
      }}
    >
      {icon}
      <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {(PILL_LABELS[label.toUpperCase()] ?? label).replace(/_/g, " ")}
      </Box>
    </Box>
  );
}

/** Two-tone bar: successes, then errors, over the employees in scope. */
export function ProgressTrack({
  success,
  error,
  total,
  height = 6,
  selected = false,
}: {
  success: number;
  error: number;
  total: number | null;
  height?: number;
  /** A shade darker on a highlighted row. */
  selected?: boolean;
}) {
  const okPct = total && total > 0 ? Math.min(100, (success / total) * 100) : 0;
  const errPct =
    total && total > 0 ? Math.max(0, Math.min(100 - okPct, (error / total) * 100)) : 0;
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        flex: "1 1 auto",
        minWidth: 40,
        height,
        borderRadius: height / 2,
        overflow: "hidden",
        display: "flex",
        bgcolor: alpha(theme.palette.text.primary, selected ? 0.12 : 0.09),
      })}
    >
      <Box sx={{ width: `${okPct}%`, bgcolor: "success.main", transition: "width .3s ease" }} />
      <Box
        sx={{
          width: `${errPct}%`,
          minWidth: error > 0 && total ? 2 : 0,
          bgcolor: "error.main",
          transition: "width .3s ease",
        }}
      />
    </Box>
  );
}

function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

export interface TaskJobListRowProps {
  name: string;
  status: { label: string; color: ChipColor; title?: string };
  success: number;
  error: number;
  /** Employees in scope; null while the ranges are provisional. */
  total: number | null;
  pendingLabel?: string;
  runningTasks: number;
  totalTasks: number;
  selected: boolean;
  onSelect: () => void;
  checked?: boolean;
  onToggleCheck?: (checked: boolean) => void;
  /** Phone: a full-width link row with a chevron and no checkbox. */
  stacked?: boolean;
}

/** One task job in the list beside its details. */
export function TaskJobListRow({
  name,
  status,
  success,
  error,
  total,
  pendingLabel = "ranges not set yet",
  runningTasks,
  totalTasks,
  selected,
  onSelect,
  checked = false,
  onToggleCheck,
  stacked = false,
}: TaskJobListRowProps) {
  const theme = useTheme();
  const pct = percentDone(success, error, total);
  const processed = success + error;
  const complete = pct === 100;
  const counts =
    total == null
      ? `${pendingLabel}${processed > 0 ? ` - ${formatCount(processed)} processed` : ""}`
      : total <= 0
        ? "nothing to process"
        : `${formatCount(processed)} / ${formatCount(total)}`;
  const body = (
    <>
      <Box
        component="span"
        title={name}
        sx={{
          fontSize: 16,
          fontWeight: 700,
          lineHeight: "22px",
          color: "text.primary",
          display: "-webkit-box",
          WebkitLineClamp: stacked ? 3 : 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          overflowWrap: "anywhere",
        }}
      >
        {name}
      </Box>
      <Box
        component="span"
        sx={{
          display: "grid",
          // One pill width for every row, so the bars and the counts line up.
          gridTemplateColumns: stacked ? "auto minmax(40px, 1fr) 44px" : "124px minmax(40px, 1fr) 44px",
          alignItems: "center",
          columnGap: "10px",
          rowGap: stacked ? 1 : 0.75,
          width: "100%",
        }}
      >
        <StatusPill label={status.label} color={status.color} title={status.title} minWidth={stacked ? 92 : 124} />
        <ProgressTrack success={success} error={error} total={total} selected={selected} />
        <Box
          component="span"
          sx={{
            textAlign: "right",
            fontSize: 13,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: complete ? "success.main" : "text.secondary",
          }}
        >
          {pct == null ? "-" : `${pct}%`}
        </Box>
        <Box
          component="span"
          sx={{
            gridColumn: "1 / -1",
            fontSize: 13,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
            ...(stacked
              ? { display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1.5, rowGap: "2px" }
              : {
                  // Fixed columns, so counts, errors and tasks sit under each other in every row.
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 0.8fr)",
                  alignItems: "baseline",
                  columnGap: "8px",
                }),
          }}
        >
          <Box
            component="span"
            title={counts}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...(!stacked && total == null && error === 0 ? { gridColumn: "1 / 3" } : {}),
            }}
          >
            {counts}
          </Box>
          {error > 0 ? (
            <Box
              component="span"
              title={`${formatCount(error)} ${error === 1 ? "error" : "errors"}`}
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "error.main", fontWeight: 600 }}
            >
              {formatCount(error)} {error === 1 ? "error" : "errors"}
            </Box>
          ) : !stacked && total != null ? (
            <Box component="span" />
          ) : null}
          <Box
            component="span"
            title={`${runningTasks} of ${totalTasks} tasks running`}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: runningTasks > 0 ? "text.secondary" : "text.disabled",
              ...(stacked ? { ml: "auto" } : {}),
            }}
          >
            {runningTasks}/{totalTasks} tasks
          </Box>
        </Box>
      </Box>
    </>
  );

  if (stacked) {
    return (
      <ButtonBase
        onClick={onSelect}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "stretch",
          gap: 1,
          width: "100%",
          py: 1.5,
          pl: 2,
          pr: 1,
          textAlign: "left",
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          {body}
        </Box>
        <ChevronRight sx={{ flexShrink: 0, mt: "1px", color: "action.active" }} />
      </ButtonBase>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : "transparent",
        "&:hover": selected ? undefined : { bgcolor: alpha(theme.palette.primary.main, 0.04) },
      }}
    >
      {onToggleCheck && (
        <Checkbox
          size="small"
          checked={checked}
          onChange={(event) => onToggleCheck(event.target.checked)}
          inputProps={{ "aria-label": `Select ${name}` }}
          sx={{ p: 0, mt: "2px" }}
        />
      )}
      <ButtonBase
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.75,
          textAlign: "left",
          borderRadius: 1,
        }}
      >
        {body}
      </ButtonBase>
    </Box>
  );
}

const RING_RADIUS = 11;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** A task job on the collapsed list: progress ring, status dot. */
export function TaskJobRailItem({
  name,
  status,
  success,
  error,
  total,
  selected,
  onSelect,
}: {
  name: string;
  status: { label: string; color: ChipColor };
  success: number;
  error: number;
  total: number | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const theme = useTheme();
  const pct = percentDone(success, error, total);
  const label = `${name} · ${status.label.replace(/_/g, " ").toLowerCase()}${pct == null ? "" : ` · ${pct}%`}`;
  const okShare = total && total > 0 ? Math.min(1, success / total) : 0;
  const errShare = total && total > 0 ? Math.max(0, Math.min(1 - okShare, error / total)) : 0;
  const bg = selected ? alpha(theme.palette.primary.main, 0.08) : "transparent";
  return (
    <Tooltip title={label} placement="right">
      <ButtonBase
        onClick={onSelect}
        aria-label={label}
        aria-current={selected ? "true" : undefined}
        sx={{
          position: "relative",
          flex: "0 0 auto",
          width: 38,
          height: 38,
          borderRadius: "8px",
          bgcolor: bg,
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
        }}
      >
        <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
          <circle
            cx="14"
            cy="14"
            r={RING_RADIUS}
            fill="none"
            stroke={alpha(theme.palette.text.primary, 0.14)}
            strokeWidth="3"
          />
          <circle
            cx="14"
            cy="14"
            r={RING_RADIUS}
            fill="none"
            stroke={theme.palette.success.main}
            strokeWidth="3"
            strokeDasharray={`${okShare * RING_LENGTH} ${RING_LENGTH}`}
            transform="rotate(-90 14 14)"
          />
          {errShare > 0 && (
            <circle
              cx="14"
              cy="14"
              r={RING_RADIUS}
              fill="none"
              stroke={theme.palette.error.main}
              strokeWidth="3"
              strokeDasharray={`${Math.max(1, errShare * RING_LENGTH)} ${RING_LENGTH}`}
              transform={`rotate(${okShare * 360 - 90} 14 14)`}
            />
          )}
        </svg>
        {status.color !== "success" && (
          <Box
            component="span"
            aria-hidden
            sx={{
              position: "absolute",
              top: 3,
              right: 3,
              width: 9,
              height: 9,
              boxSizing: "border-box",
              borderRadius: "50%",
              bgcolor: statusToneColor(theme, status.color),
              border: 2,
              borderColor: selected ? alpha(theme.palette.primary.main, 0.08) : "background.paper",
            }}
          />
        )}
      </ButtonBase>
    </Tooltip>
  );
}

export interface JobStat {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** A dot in this colour before the label. */
  dot?: string;
  icon?: ReactNode;
  tone?: "info";
  valueColor?: string;
}

/** The job's counters as a row of labelled cells (a 2x2 grid on a phone). */
export function JobStatsList({ items, grid = false }: { items: JobStat[]; grid?: boolean }) {
  const theme = useTheme();
  const infoBg = alpha(theme.palette.info.main, 0.1);
  const infoFg = theme.palette.info.dark;
  return (
    <Box
      component="dl"
      aria-label="Job statistics"
      sx={
        grid
          ? {
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              m: 0,
              mt: 1,
              border: 1,
              borderColor: "divider",
              borderRadius: "6px",
              overflow: "hidden",
            }
          : {
              flex: "0 0 auto",
              display: "flex",
              alignItems: "stretch",
              m: 0,
              borderRight: 1,
              borderColor: "divider",
            }
      }
    >
      {items.map((item, index) => (
        <Box
          key={item.key}
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "2px",
            bgcolor: item.tone === "info" ? infoBg : undefined,
            ...(grid
              ? {
                  px: 1.5,
                  py: 1,
                  // An odd last cell takes the whole row rather than leave a hole.
                  ...(index === items.length - 1 && index % 2 === 0
                    ? { gridColumn: "1 / -1", borderRight: 0 }
                    : { borderRight: index % 2 === 0 ? 1 : 0 }),
                  borderBottom: index < items.length - 2 ? 1 : 0,
                  borderColor: "divider",
                }
              : { px: "14px", py: 0.5, borderLeft: 1, borderColor: "divider" }),
          }}
        >
          <Box
            component="dt"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              color: item.tone === "info" ? infoFg : "text.secondary",
              "& svg": { fontSize: 12 },
            }}
          >
            {item.dot ? (
              <Box
                component="span"
                aria-hidden
                sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.dot, flexShrink: 0 }}
              />
            ) : null}
            {item.icon}
            {item.label}
          </Box>
          <Box
            component="dd"
            sx={{
              m: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 16,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              color: item.valueColor ?? (item.tone === "info" ? infoFg : "text.primary"),
            }}
          >
            {item.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
